import { useTaxonomies } from '@gik/api/give/taxonomies';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/CustomHooks';
import type { WordpressTaxonomy } from '@gik/core/models/wordpress/WordpressTaxonomy';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import type { GiveTaxonomyType } from '@gik/shop/types/GiveTaxonomyType';
import { Button } from '@gik/ui-legacy/Button';
import { Grid } from '@gik/ui-legacy/Grid';
import type { SelectOptionsType } from '@gik/ui-legacy/Select';
import { Select } from '@gik/ui-legacy/Select';
import { ButtonBone } from '@gik/ui-legacy/SkeletonLoader';
import { DivBone } from '@gik/ui-legacy/SkeletonLoader/Bones/DivBone';
import { ApiErrorDisplay } from '@gik/ui-legacy/gik/ApiErrorDisplay';
import React from 'react';
import { GiveSituationTile } from '../SituationsGrid/GiveSituationTile';
import type { GiveSituationsGridProps } from '../SituationsGrid/GiveSituationsGrid';
import { GiveSituationsGrid } from '../SituationsGrid/GiveSituationsGrid';
import { SituationsNav } from '../SituationsNav';

export type SituationsGridWithNavProps = {
  items?: WordpressTaxonomy[];
  loadMoreButton?: boolean;
  taxonomyType: GiveTaxonomyType;
  parentTaxonomy: string;
} & UIComponent;

function SituationsGridWithNavComp({
  taxonomyType,
  parentTaxonomy,
  items,
  loadMoreButton,
}: SituationsGridWithNavProps): React.ReactElement {
  const bem = useBemCN('situations-grid-with-nav');
  const isSmUp = useBreakpoint(Breakpoint.SM);

  const initialItem = items.find(item => item.slug === parentTaxonomy);
  let initialSelectedId = '-1';
  if (initialItem) initialSelectedId = initialItem.id.toString();

  const [selectedId, setSelectedId] = React.useState<string>(initialSelectedId);

  const { data: situations, error: situationsError } = useTaxonomies(
    'situation',
    { page: 1, perPage: 100 },
    {
      fallbackData: items,
    }
  );

  const situationCategories = situations?.filter(item => item.parent === 0);
  let childSituations = situations?.filter(item => item.parent !== 0);

  if (selectedId !== '-1') {
    childSituations = childSituations?.filter(item => item.parent.toString() === selectedId);
  }

  const selectOptions: SelectOptionsType[] = situationCategories?.map(item => {
    return {
      label: item.name,
      value: item.id.toString(),
      image: item.acf.taxonomy_svg_icon,
    };
  });

  function handleNavChange(value: number | string) {
    // this needs to perform a super shallow link...
    // we just need the url to change but not trigger SSR or a route reload
    // TODO update navigateTo and Link component to support this
    const item = items.find(item => item.id.toString() === value);
    if (item) {
      history.pushState(null, null, `/${taxonomyType}/${item.slug}`);
    } else {
      history.pushState(null, null, `/${taxonomyType}`);
    }

    setSelectedId(value !== undefined ? value.toString() : '-1');
  }

  selectOptions?.unshift({
    label: 'Show All',
    value: '-1',
  });

  if (situationsError) return <ApiErrorDisplay />;

  return (
    <div {...bem()}>
      {isSmUp && <SituationsNav items={situationCategories} value={parseInt(selectedId)} onChange={handleNavChange} />}
      {!isSmUp && <Select options={selectOptions} value={selectedId} onChange={handleNavChange} />}
      <GiveSituationsGrid
        cols={4}
        skeletonItems={12}
        perPage={12}
        loadMoreButton={loadMoreButton}
        items={childSituations}
        situations={situations}
      />
    </div>
  );
}

export const SituationsGridWithNav = withComponentErrorBoundary(SituationsGridWithNavComp);

export const GiveSituationsGridWithNavSkeleton = ({
  skeletonItems = 10,
  skeletonCategoryItems = 5,
  cols = 5,
  loadMoreButton,
}: Partial<GiveSituationsGridProps>): React.ReactElement => {
  const bem = useBemCN(`give-situations-grid`);
  const isSmUp = useBreakpoint(Breakpoint.SM);

  return (
    <div {...bem()}>
      {isSmUp && (
        <Grid cols={cols} {...bem('nav')}>
          {Array.from(Array(skeletonCategoryItems).keys()).map(index => {
            return <ButtonBone pill key={index} />;
          })}
        </Grid>
      )}
      {!isSmUp && <DivBone {...bem('select')} />}
      <Grid cols={cols} {...bem('grid')}>
        {Array.from(Array(skeletonItems).keys()).map(index => {
          return <GiveSituationTile key={index} />;
        })}
      </Grid>
      {loadMoreButton && (
        <Button variant="default" pill disabled {...bem('btn-more')}>
          Show All
        </Button>
      )}
    </div>
  );
};
